import React from 'react';
import { Link } from 'react-router-dom';
import './HacktopiaPage.css';
import Seo from "./components/Seo";

const HacktopiaPage = () => {
    return (
        <div className="hacktopia-page">
            <Seo
                title="Hacktopia — Student AI Hackathons by NYX NoCode"
                description="Run a school-wide AI hackathon in weeks. Teacher-led, student-centered, and proven to spark real creativity."
            />
            <header className="hacktopia-header">
                <h1>NoCode Hackathons</h1>
            </header>

            <section className="hacktopia-section">
                <div className="hacktopia-highlight">
                    <h2>The Idea</h2>
                    <p>
                        Hacktopia brings the energy of a hackathon into the classroom.
                        Students build apps, websites, and interactive projects in real time—no coding required.
                    </p>
                </div>
            </section>

            <section className="hacktopia-section">
                <div className="hacktopia-highlight">
                    <h2>The Engine: NYX NoCode</h2>
                    <p>
                        Developed by software engineer and author Karen Kilroy, NYX NoCode transforms simple prompts into working software:
                    </p>
                    <ul>
                        <li>Text → prototype in seconds</li>
                        <li>Doodle → interactive design</li>
                        <li>No syntax, no barrier to entry</li>
                    </ul>
                </div>
            </section>

            <section className="hacktopia-section">
                <div className="hacktopia-highlight">
                    <h2>The Guide: Justin Mascot</h2>
                    <p>
                        Every event is co-hosted by Justin Mascot—a generative AI persona that adopts the school's mascot identity.
                    </p>
                    <ul>
                        <li>Motivates teams</li>
                        <li>Provides feedback</li>
                        <li>Keeps students engaged</li>
                    </ul>
                </div>
            </section>

            <section className="hacktopia-section">
                <div className="hacktopia-highlight">
                    <h2>How It Works</h2>
                    <ul>
                        <li><strong>Our team is virtual</strong> — we provide the platform, framework, and support</li>
                        <li><strong>Held in class</strong> — teachers organize students into small teams</li>
                        <li><strong>Teachers lead hands-on</strong> — guiding, learning, and creating alongside the kids</li>
                        <li><strong>Kickoff</strong> — meet Justin Mascot</li>
                        <li><strong>Workshops</strong> — fast, engaging intros to no-code tools</li>
                        <li><strong>Challenges</strong> — teams design apps to solve real or imaginative problems</li>
                        <li><strong>Showcase</strong> — a fun celebration of student work</li>
                    </ul>
                </div>
            </section>

            <section className="hacktopia-section">
                <div className="hacktopia-highlight">
                    <h2>Why It Works</h2>
                    <ul>
                        <li><strong>Natural Language Interface</strong> — students build with prompts</li>
                        <li><strong>Professional Output</strong> — clean, responsive, customizable apps</li>
                        <li><strong>Built-In Creativity</strong> — remix ideas or start from scratch</li>
                        <li><strong>Multi-Language Support</strong> — accessible for diverse learners</li>
                    </ul>
                </div>
            </section>

            <section className="hacktopia-section">
                <h2>Leveling the Playing Field</h2>
                <p>
                    Traditional programming is something only a few students excel at. For everyone else, the barriers—syntax, debugging, complexity—keep them from fully participating.
                </p>
                <p>
                    <strong>NYX NoCode removes those barriers.</strong>
                </p>
                <p>
                    Now every student, regardless of background or skill level, can take part in building apps, solving problems, and expressing creativity with technology.
                </p>
            </section>

            <section className="hacktopia-section">
                <h2>Program Options</h2>
                
                <div className="program-option">
                    <h3>Demo Day — $500</h3>
                    <ul>
                        <li>For up to 200 students</li>
                        <li>One-day classroom event</li>
                        <li>Introduction to AI + prompt exploration</li>
                        <li>Teachers lead, students create, Justin Mascot guides</li>
                        <li>Fast, fun, and accessible</li>
                    </ul>
                </div>

                <div className="program-option">
                    <h3>Hacktopia — $5,000</h3>
                    <ul>
                        <li>A 21-day classroom experience for up to 200 students</li>
                        <li>Teams build apps and projects with NYX NoCode</li>
                        <li>Entries judged + student certificates awarded</li>
                        <li>Option to organize community prizes for added excitement</li>
                        <li>Engaging, structured, and transformative</li>
                    </ul>
                </div>

                <div className="program-option">
                    <h3>Extended Access</h3>
                    <p>
                        Want more? Special arrangements are available for schools that wish to integrate NYX NoCode beyond a single event, bringing long-term no-code AI creation into the classroom.
                    </p>
                </div>
            </section>

            <section className="hacktopia-section">
                <h2>The Outcome</h2>
                <p>
                    Hacktopia isn't about winning—it's about launching students into creation. With NYX NoCode and Justin Mascot providing the magic, teachers guide the journey, and classrooms transform into creative studios.
                </p>
                <p className="highlight">🌟 Every prompt is a starting line.</p>
            </section>

            <section className="hacktopia-section get-started-cta">
                <h2>Ready to Get Started?</h2>
                <p>Bring the excitement of AI-powered creation to your classroom. Whether you want a Demo Day or the full Hacktopia experience, we're here to help you get started.</p>
                <Link to="/get-started" className="cta-button">
                    🚀 Get Started with Hacktopia
                </Link>
            </section>
        </div>
    );
};

export default HacktopiaPage;